/**
 * The pure core of the run's progress (deliverable 7): `import.progress` events of this run and
 * polls of `import.runs.get` reduce to one state. The state records where the last word came from
 * (`event` or `poll`) so a spec can see which path drove the bar.
 */
import type { ImportRunStatus, ImportRunV1 } from '@codex/contracts';

export type ProgressPhase = 'upload' | 'plan' | 'apply' | 'undo';
export type ProgressSource = 'event' | 'poll';

export interface ProgressState {
  readonly phase: ProgressPhase | null;
  readonly done: number;
  readonly total: number;
  readonly status: ImportRunStatus | null;
  readonly source: ProgressSource | null;
}

export const INITIAL_PROGRESS: ProgressState = {
  phase: null,
  done: 0,
  total: 0,
  status: null,
  source: null,
};

export interface ProgressEventData {
  readonly runId: string;
  readonly phase: 'plan' | 'apply' | 'undo';
  readonly done: number;
  readonly total: number;
  readonly status: ImportRunStatus;
}

export type ProgressInput =
  | { readonly kind: 'event'; readonly data: ProgressEventData }
  | {
      readonly kind: 'poll';
      readonly run: Pick<ImportRunV1, 'id' | 'status' | 'partsReceived' | 'partsTotal'>;
    };

/** The phase a status belongs to, or null when none is running. */
export function phaseOfStatus(status: ImportRunStatus): ProgressPhase | null {
  switch (status) {
    case 'uploading':
      return 'upload';
    case 'planning':
      return 'plan';
    case 'applying':
      return 'apply';
    case 'undoing':
      return 'undo';
    default:
      return null;
  }
}

/** Statuses whose run is worked on by a job or an upload: the page polls while one holds. */
export function isActiveStatus(status: ImportRunStatus): boolean {
  return phaseOfStatus(status) !== null;
}

/** Folds one input into the state; an event of another run changes nothing. */
export function reduceProgress(
  state: ProgressState,
  input: ProgressInput,
  runId: string,
): ProgressState {
  if (input.kind === 'event') {
    if (input.data.runId !== runId) return state;
    const { phase, done, total, status } = input.data;
    return { phase, done, total, status, source: 'event' };
  }
  if (input.run.id !== runId) return state;
  const { status } = input.run;
  const phase = phaseOfStatus(status) ?? state.phase;
  if (status === 'uploading') {
    return {
      phase: 'upload',
      done: input.run.partsReceived,
      total: input.run.partsTotal,
      status,
      source: 'poll',
    };
  }
  // A poll carries no counts for the other phases: keep what an event said about the same phase.
  const keep = phase === state.phase && state.status === status;
  return {
    phase,
    done: keep ? state.done : 0,
    total: keep ? state.total : 0,
    status,
    source: 'poll',
  };
}

/** True when the status moved: the run's queries are then refetched. */
export function statusChanged(before: ProgressState, after: ProgressState): boolean {
  return before.status !== null && after.status !== null && after.status !== before.status;
}
